package com.writerslab

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.dom.Img

// ──────────────────────────────────────────────────────────────────────
// Style helpers. Compose HTML doesn't have a Color class, so colours are
// plain hex strings passed via property("color"|"background-color"|...).
// ──────────────────────────────────────────────────────────────────────

fun StyleScope.fg(color: String) = property("color", color)
fun StyleScope.bg(color: String) = property("background-color", color)
fun StyleScope.border(width: String, style: String, color: String) = property("border", "$width $style $color")

fun StyleScope.box(
    bgColor: String = WLColors.SurfaceContainerLow,
    radiusPx: Int = 8,
    paddingPx: Int = 16,
) {
    bg(bgColor)
    property("border-radius", "${radiusPx}px")
    property("padding", "${paddingPx}px")
}

fun StyleScope.flexCol(gapPx: Int = 0) {
    display(DisplayStyle.Flex)
    property("flex-direction", "column")
    if (gapPx > 0) property("gap", "${gapPx}px")
}

fun StyleScope.flexRow(gapPx: Int = 0, align: AlignItems = AlignItems.Center) {
    display(DisplayStyle.Flex)
    property("flex-direction", "row")
    alignItems(align)
    if (gapPx > 0) property("gap", "${gapPx}px")
}

fun StyleScope.fillWidth() = property("width", "100%")

enum class DragonTint { GOLD, WHITE, DARK }

/** Inline dragon icon — the «credit» unit symbol used wherever the
 *  app shows a credit count. The source PNG ships as a black outline;
 *  the filter chain colour-shifts it to the requested tint so it can
 *  ride next to either the gold credit numeral on dark surfaces,
 *  white text on dark surfaces, or dark text on the gold paid-CTA
 *  background. Filter recipes via https://codepen.io/sosuke/pen/Pjoqqp. */
@Composable
fun DragonIcon(sizePx: Int = 18, tint: DragonTint = DragonTint.GOLD) {
    val filter = when (tint) {
        DragonTint.GOLD -> "invert(75%) sepia(45%) saturate(2000%) " +
            "hue-rotate(360deg) brightness(102%) contrast(93%)"
        DragonTint.WHITE -> "invert(100%) brightness(95%)"
        DragonTint.DARK -> "none"
    }
    Img(src = "dragon.png", alt = "") {
        attr(
            "style",
            "width:${sizePx}px;height:${sizePx}px;display:inline-block;" +
                "vertical-align:middle;filter:$filter;"
        )
    }
}

fun StyleScope.maxW(px: Int) {
    property("max-width", "${px}px")
    property("width", "100%")
}

fun StyleScope.clickableCursor() {
    property("cursor", "pointer")
    property("user-select", "none")
    // `<md-ripple>` (placed as the host's last child) positions itself with
    // `inset: 0` and needs the host to be a positioning context. md-ripple
    // does its own clipping inside its shadow DOM, so the host doesn't need
    // `overflow: hidden` (and we can't add it — the compact metric carousel
    // uses `clickableCursor` plus `overflow-x: auto`).
    property("position", "relative")
}

// ──────────────────────────────────────────────────────────────────────
// Typography presets keyed to roughly the previous Material3 scale.
// ──────────────────────────────────────────────────────────────────────

enum class TextLevel(val sizePx: Int, val linePx: Int, val weight: Int) {
    HeadlineLarge(28, 36, 600),
    HeadlineSmall(22, 30, 600),
    TitleMedium(18, 26, 600),
    TitleSmall(16, 22, 500),
    BodyLarge(18, 26, 400),
    BodyMedium(16, 24, 400),
    BodySmall(14, 20, 400),
    LabelLarge(16, 22, 500),
    LabelSmall(13, 18, 500),
}

fun StyleScope.typography(level: TextLevel) {
    property("font-size", "${level.sizePx}px")
    property("line-height", "${level.linePx}px")
    property("font-weight", level.weight.toString())
    property(
        "letter-spacing",
        if (level == TextLevel.HeadlineLarge || level == TextLevel.HeadlineSmall) "0" else "0.15px",
    )
}
